import type { MyWorkspace } from "@minga/protocol"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import { newWorkspaceSlug, ROLE_LABEL } from "@/features/workspaces/workspace-model"
import { authClient, unwrap } from "@/lib/auth-client"
import { workspacesQuery } from "@/lib/queries"
import { cn } from "@/lib/utils"
import { switchTarget } from "@/lib/workspace-switch"
import { ErrorText } from "./ui/alert"
import { Button } from "./ui/button"
import { CogIcon } from "./ui/icons"
import { Input } from "./ui/input"
import { MenuSeparator, menuItemClass, Popover } from "./ui/popover"
import { WorkspaceTile } from "./workspace-tile"

/** Sorted for the picker: the current workspace first, then by name. */
export const pickerOrder = (
  list: ReadonlyArray<MyWorkspace>,
  currentId: string,
): ReadonlyArray<MyWorkspace> =>
  [...list].sort(
    (a, b) =>
      Number(b.id === currentId) - Number(a.id === currentId) ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  )

/**
 * The header's workspace picker (earlier spec §1.3, UX §3.3). It only navigates (D2): the workspace route records
 * the switch. Rows keep your place where that makes sense (`switchTarget`).
 */
export function WorkspacePicker({
  workspace,
  inProject = false,
}: {
  workspace: MyWorkspace
  /**
   * Inside a project (UX pass phase 2, P14): the project header has no Home or Knowledge tab, so the picker is the
   * workspace's menu: Home and Knowledge are rows at every width.
   */
  inProject?: boolean | undefined
}) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [filter, setFilter] = useState("")
  const list = useQuery(workspacesQuery).data?.workspaces ?? [workspace]
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const target = switchTarget(pathname)
  const shown = pickerOrder(list, workspace.id).filter(
    (w) => !filter || w.name.toLowerCase().includes(filter.trim().toLowerCase()),
  )
  const close = () => {
    setOpen(false)
    setCreating(false)
    setFilter("")
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
      title="Switch workspace"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={`Workspace: ${workspace.name}. Switch workspace`}
          className="tap flex h-8 max-w-full min-w-0 items-center gap-1 rounded-control px-2 text-muted hover:bg-fill hover:text-ink"
        >
          <span className="max-w-[16ch] truncate">{workspace.name}</span>
          <Caret />
        </button>
      )}
    >
      {creating ? (
        <CreateWorkspaceForm onCancel={() => setCreating(false)} onCreated={close} />
      ) : (
        <>
          {list.length > 8 && (
            <Input
              aria-label="Find a workspace"
              placeholder="Find a workspace"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="mb-1"
            />
          )}
          {shown.map((w) => (
            <Link
              key={w.id}
              role="menuitem"
              to={target.to}
              params={{ workspaceId: w.id }}
              onClick={close}
              className={menuItemClass}
            >
              <WorkspaceTile name={w.name} />
              <span className="min-w-0 flex-1 truncate">{w.name}</span>
              <span className="text-[12px] text-muted">{ROLE_LABEL[w.role]}</span>
              <span aria-hidden="true" className={cn("w-3 text-ink", w.id !== workspace.id && "invisible")}>
                ✓
              </span>
              {w.id === workspace.id && <span className="sr-only">(current)</span>}
            </Link>
          ))}
          <MenuSeparator />
          <button type="button" role="menuitem" className={menuItemClass} onClick={() => setCreating(true)}>
            <span aria-hidden="true" className="w-6 text-center text-muted">
              +
            </span>
            Create workspace
          </button>
          {inProject && (
            <Link
              role="menuitem"
              to="/w/$workspaceId"
              params={{ workspaceId: workspace.id }}
              activeOptions={{ exact: true }}
              onClick={close}
              className={menuItemClass}
            >
              <span aria-hidden="true" className="w-6 text-center text-muted">
                ⌂
              </span>
              Home
            </Link>
          )}
          {/* decision 0086: on a phone the header has no Knowledge tab; it is a row here. UX pass 2026-10-15: on a
              workspace page only there (md:hidden), the header's tab is the entry; inside a project at every width */}
          <Link
            role="menuitem"
            to="/w/$workspaceId/knowledge"
            params={{ workspaceId: workspace.id }}
            onClick={close}
            className={cn(menuItemClass, !inProject && "md:hidden")}
          >
            <span aria-hidden="true" className="w-6 text-center text-muted">
              ¶
            </span>
            Knowledge
          </Link>
          <Link
            role="menuitem"
            to="/w/$workspaceId/settings/general"
            params={{ workspaceId: workspace.id }}
            onClick={close}
            className={menuItemClass}
          >
            <span aria-hidden="true" className="flex w-6 justify-center text-muted">
              <CogIcon size={16} />
            </span>
            Workspace settings
          </Link>
          {(workspace.role === "owner" || workspace.role === "admin") && (
            <Link
              role="menuitem"
              to="/w/$workspaceId/settings/members"
              params={{ workspaceId: workspace.id }}
              search={{ invite: 1 }}
              onClick={close}
              className={menuItemClass}
            >
              <span aria-hidden="true" className="w-6 text-center text-muted">
                @
              </span>
              Invite people
            </Link>
          )}
        </>
      )}
    </Popover>
  )
}

/** Inline "New workspace" form (UX §3.3): name → slug with a random suffix, then open it. */
export function CreateWorkspaceForm({
  onCancel,
  onCreated,
}: {
  onCancel?: () => void
  onCreated?: () => void
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState("")
  const create = useMutation({
    mutationFn: async (value: string) =>
      unwrap(
        await authClient.organization.create({
          name: value,
          slug: newWorkspaceSlug(value),
          keepCurrentActiveOrganization: true,
        }),
      ),
    onSuccess: async (org) => {
      await queryClient.invalidateQueries({ queryKey: workspacesQuery.queryKey })
      onCreated?.()
      if (org) await navigate({ to: "/w/$workspaceId", params: { workspaceId: org.id } })
    },
  })
  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    const value = name.trim()
    if (!value || value.length > 48) return
    create.mutate(value)
  }
  return (
    <form className="flex flex-col gap-2 p-1.5" onSubmit={onSubmit}>
      <div className="font-semibold">New workspace</div>
      <Input
        aria-label="Workspace name"
        placeholder="Every"
        value={name}
        maxLength={48}
        autoFocus
        onChange={(e) => setName(e.target.value)}
      />
      {name.trim() && (
        <p className="font-mono text-[12px] text-muted">
          slug: {newWorkspaceSlug(name, () => 0).slice(0, -5)}-…
        </p>
      )}
      {create.error && <ErrorText>Couldn't create the workspace. {create.error.message}</ErrorText>}
      <div className="flex justify-end gap-1.5">
        {onCancel && (
          <Button variant="ghost" size="md" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" size="md" disabled={!name.trim() || create.isPending}>
          {create.isPending ? "Creating…" : "Create"}
        </Button>
      </div>
    </form>
  )
}

export const Caret = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="shrink-0"
  >
    <path d="M3 4.5l3 3 3-3" />
  </svg>
)
